import type {
	BackupCodeSchema,
	CompactProxyOutput,
	CompactSenderGroupOutput,
	CookieAuth,
	GAuthSchema,
	IMAPSchema,
	SenderBatchItem,
} from "@/api/custom_types";
import type { ColumnSpec } from "@/constants/types/csv";
import type { RowData } from "@/pages/senders/New/CSVSender/Index";
import {
	allowEmpty,
	applyRowIsValid,
	parseBackupCodes,
	stripSlash,
} from "@/utils/csv-helpers";
import type { TranslationF } from "@/utils/type-utils";

export function isRowEmpty(row: RowData): boolean {
	return (
		(row.email.value == null || row.email.value === "") &&
		(row.password.value == null || row.password.value === "") &&
		(row.proxyIp.value == null || row.proxyIp.value === "") &&
		(row.senderGroupName.value == null || row.senderGroupName.value === "") &&
		(row.backupCode.value == null || row.backupCode.value === "") &&
		(row.gauth.value == null || row.gauth.value === "") &&
		(row.imapServer.value == null || row.imapServer.value === "") &&
		(row.imapUsername.value == null || row.imapUsername.value === "") &&
		(row.imapPassword.value == null || row.imapPassword.value === "") &&
		(row.imapSsl.value == null || row.imapSsl.value === "") &&
		(row.imapPort.value == null || row.imapPort.value === 0)
	);
}

export function isRowValid(row: RowData): boolean {
	if (isRowEmpty(row)) return true;

	const isMainFieldsValid =
		row.email.value &&
		row.email.value !== "" &&
		row.password.value &&
		row.password.value !== "";

	const hasBackupOrGauth =
		(row.backupCode.value && row.backupCode.value !== "") ||
		(row.gauth.value && row.gauth.value !== "");
	const hasCompleteImap =
		row.imapServer.value != null &&
		row.imapServer.value !== "" &&
		row.imapUsername.value != null &&
		row.imapUsername.value !== "" &&
		row.imapPassword.value != null &&
		row.imapPassword.value !== "" &&
		row.imapSsl.value != null &&
		row.imapSsl.value !== "" &&
		row.imapPort.value != null &&
		row.imapPort.value !== 0;

	const isAuthValid = hasBackupOrGauth || hasCompleteImap;

	return isMainFieldsValid && isAuthValid;
}

export function createSenderBatchItems(
	rows: RowData[],
	groups: CompactSenderGroupOutput[],
	proxies: CompactProxyOutput[],
): SenderBatchItem[] {
	const groupByName = new Map(groups.map((g) => [g.name, g.id]));
	const proxyByAddrPort = new Map(
		proxies.map((p) => {
			const cleanAddr = stripSlash(p.address);
			return [`${cleanAddr}:${p.port}`, p.id];
		}),
	);

	return rows
		.filter((row) => !isRowEmpty(row))
		.map((row) => {
			let auth: GAuthSchema | IMAPSchema | BackupCodeSchema | CookieAuth;

			if (row.gauth.value) {
				auth = { type: "gauth", auth: row.gauth.value } as GAuthSchema;
			} else if (
				row.imapServer.value &&
				row.imapUsername.value &&
				row.imapPassword.value &&
				row.imapSsl.value != null &&
				row.imapPort.value != null
			) {
				auth = {
					type: "imap",
					server: row.imapServer.value,
					username: row.imapUsername.value,
					password: row.imapPassword.value,
					port: row.imapPort.value,
					ssl: Boolean(row.imapSsl.value),
				} as IMAPSchema;
			} else if (row.backupCode.value) {
				auth = {
					type: "backup_code",
					auth: parseBackupCodes(String(row.backupCode.value)),
				} as BackupCodeSchema;
			} else {
				auth = { type: "cookie" } as CookieAuth;
			}

			const proxyKey: string = row.proxyIp.value
				? row.proxyIp.value.toString()
				: "";
			const groupKey: string = row.senderGroupName.value
				? row.senderGroupName.value.toString()
				: "";
			const proxyId = proxyKey ? proxyByAddrPort.get(proxyKey) ?? null : null;
			const groupId = groupKey ? groupByName.get(groupKey) ?? null : null;

			const priorityUsage =
				row.senderPriority.value === "INHERITED" ||
				row.senderPriority.value == null
					? null
					: row.senderPriority.value!.toString();

			return {
				platform: "ps",
				ea_email: row.email.value!.toString(),
				ea_password: row.password.value!.toString(),
				proxy_id: proxyId,
				sender_group_id: groupId,
				priority_usage: priorityUsage,
				auth,
			};
		});
}

export const makeColumnSpecs = (
	t: TranslationF,
	proxySource: string[],
	groupSource: string[],
	priorityOptions: string[],
): ColumnSpec[] => [
	{
		key: "email.value",
		header: t("addSender.csv.headerEmail"),
		width: "234px",
		type: "text",
		validator: allowEmpty((v) =>
			typeof v === "string" && /^\S+@\S+\.\S+$/.test(v)
				? true
				: t("validation.email"),
		),
	},
	{
		key: "password.value",
		header: t("addSender.csv.headerPassword"),
		width: "150px",
		type: "text",
		validator: allowEmpty((v) => {
			if (typeof v !== "string") {
				return t("validation.string", {
					field: t("addSender.csv.headerPassword"),
				});
			}
			if (v.length < 6) {
				return t("validation.atLeast", {
					field: t("addSender.csv.headerPassword"),
					num: 6,
				});
			}
			if (!/[a-z]/.test(v) || !/[A-Z]/.test(v)) {
				return t("validation.containUppercaseAndLowercase", {
					field: t("addSender.csv.headerPassword"),
				});
			}
			return true;
		}),
	},
	{
		key: "proxyIp.value",
		header: t("addSender.csv.headerProxyIp"),
		width: "400px",
		type: "autocomplete",
		source: proxySource,
		validator: allowEmpty((v) => {
			if (!proxySource.includes(v)) {
				return t("validation.selectAvailable", {
					field: t("addSender.csv.headerProxyIp"),
				});
			}
			return true;
		}),
	},
	{
		key: "senderGroupName.value",
		header: t("addSender.csv.headerSenderGroupName"),
		width: "218px",
		type: "autocomplete",
		source: groupSource,
		validator: allowEmpty((v) => {
			if (!groupSource.includes(v)) {
				return t("validation.selectAvailable", {
					field: t("addSender.csv.headerSenderGroupName"),
				});
			}
			return true;
		}),
	},
	{
		key: "senderPriority.value",
		header: t("addSender.csv.headerSenderPriority"),
		width: "160px",
		type: "dropdown",
		source: priorityOptions,
		validator: allowEmpty((v) => {
			if (!priorityOptions.includes(v)) {
				return t("validation.selectAvailable", {
					field: t("addSender.csv.headerSenderPriority"),
				});
			}
			return true;
		}),
	},
	{
		key: "backupCode.value",
		header: t("addSender.csv.headerBackupCode"),
		width: "183px",
		type: "text",
		validator: allowEmpty((v) => {
			if (typeof v !== "string") {
				return t("validation.string", {
					field: t("addSender.csv.headerBackupCode"),
				});
			}
			const codes = v.split(",").map((c) => c.trim());
			const invalid = codes.find((code) => !/^(?:\d{6}|\d{8})$/.test(code));
			return invalid
				? t("validation.backupCodeFormat", {
						field: t("addSender.csv.headerBackupCode"),
					})
				: true;
		}),
	},
	{
		key: "gauth.value",
		header: t("addSender.csv.headerGauth"),
		width: "150px",
		type: "text",
		validator: allowEmpty((_v) => true),
	},
	{
		key: "imapServer.value",
		header: t("addSender.csv.headerImapServer"),
		width: "184px",
		type: "text",
		validator: allowEmpty((_v) => true),
	},
	{
		key: "imapPort.value",
		header: t("addSender.csv.headerImapPort"),
		width: "120px",
		type: "text",
		validator: allowEmpty((v) => {
			const num = Number(v);
			if (!Number.isInteger(num) || num < 1 || num > 65535) {
				return t("validation.IntegerBetween", {
					field: t("addSender.csv.headerImapPort"),
					min: 1,
					max: 65535,
				});
			}
			return true;
		}),
	},
	{
		key: "imapUsername.value",
		header: t("addSender.csv.headerImapUsername"),
		width: "164px",
		type: "text",
		validator: allowEmpty((_v) => true),
	},
	{
		key: "imapPassword.value",
		header: t("addSender.csv.headerImapPassword"),
		width: "164px",
		type: "text",
		validator: allowEmpty((v) => {
			if (typeof v !== "string") {
				return t("validation.string", {
					field: t("addSender.csv.headerImapPassword"),
				});
			}
			if (v.length < 6) {
				return t("validation.atLeast", {
					field: t("addSender.csv.headerImapPassword"),
					num: 6,
				});
			}
			if (!/[a-z]/.test(v) || !/[A-Z]/.test(v)) {
				return t("validation.containUppercaseAndLowercase", {
					field: t("addSender.csv.headerImapPassword"),
				});
			}
			return true;
		}),
	},
	{
		key: "imapSsl.value",
		header: t("addSender.csv.headerImapSsl"),
		width: "112px",
		type: "checkbox",
		validator: allowEmpty((_v) => true),
	},
];

const validateRowData = (
	row: RowData,
	index: number,
	t: TranslationF,
): string | null => {
	if (!isRowValid(row)) {
		return t("validation.missingRowValues", { index: index + 1 });
	}

	return row.isRowValid ? "" : t("validation.rowNotValid", { row: index + 1 });
};

export const validateData = (
	data: RowData[],
	t: TranslationF,
): { isValid: boolean; errorMessage: string } => {
	if (isRowEmpty(data[0])) {
		return { isValid: false, errorMessage: "" };
	}

	for (let index = 0; index < data.length; index++) {
		const row = data[index];
		applyRowIsValid(row);
		const rowError = validateRowData(row, index, t);
		if (rowError) {
			return { isValid: false, errorMessage: rowError };
		}
	}

	return { isValid: true, errorMessage: "" };
};

export const createEmptyRow = (): RowData => {
	return {
		email: {
			value: null,
			disabled: false,
			isValid: true,
		},
		password: {
			value: null,
			disabled: false,
			isValid: true,
		},
		proxyIp: {
			value: null,
			disabled: false,
			isValid: true,
		},
		senderGroupName: {
			value: null,
			disabled: false,
			isValid: true,
		},
		senderPriority: {
			value: "INHERITED",
			disabled: false,
			isValid: true,
		},
		backupCode: {
			value: null,
			disabled: false,
			isValid: true,
		},
		gauth: {
			value: null,
			disabled: false,
			isValid: true,
		},
		imapServer: {
			value: null,
			disabled: false,
			isValid: true,
		},
		imapUsername: {
			value: null,
			disabled: false,
			isValid: true,
		},
		imapPassword: {
			value: null,
			disabled: false,
			isValid: true,
		},
		imapSsl: {
			value: null,
			disabled: false,
			isValid: true,
		},
		imapPort: {
			value: null,
			disabled: false,
			isValid: true,
		},
		isRowValid: true,
	};
};
